// Dynamically determine base URL based on current page protocol
// This ensures HTTPS is used when page is loaded over HTTPS
const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return '';
};

class ApiClient {
  async request(endpoint, options = {}) {
    const API_BASE_URL = getBaseURL();
    const url = `${API_BASE_URL}${endpoint}`;
    
    const timeout = options.timeout || 30000; // 30s default
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    // Get auth token from localStorage
    const token = localStorage.getItem('access_token');
    
    const config = {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        
        // 401 Unauthorized: Token ungültig oder abgelaufen
        if (response.status === 401) {
          console.error('401 Unauthorized - clearing tokens and redirecting to login');
          localStorage.removeItem('access_token');
          localStorage.removeItem('cashx_user');
          localStorage.removeItem('cashx_tenant');
          localStorage.removeItem('cashx_impersonation');
          
          // Redirect to login
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          throw new Error('Nicht authentifiziert');
        }
        
        // User-friendly error messages
        if (response.status >= 500) {
          throw new Error('Server-Fehler. Bitte versuchen Sie es später erneut.');
        } else if (response.status === 404) {
          throw new Error('Nicht gefunden.');
        } else if (response.status === 400) {
          throw new Error(error.detail || 'Ungültige Anfrage.');
        } else if (response.status === 403) {
          throw new Error(error.detail || 'Zugriff verweigert.');
        }
        
        throw new Error(error.detail || response.statusText);
      }

      // For file downloads
      if (options.responseType === 'blob') {
        return await response.blob();
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Network errors
      if (error.name === 'AbortError') {
        throw new Error('Zeitüberschreitung. Bitte prüfen Sie Ihre Verbindung.');
      } else if (error.message === 'Failed to fetch') {
        throw new Error('Netzwerkfehler. Bitte prüfen Sie Ihre Verbindung.');
      }
      
      console.error('API Error:', error);
      throw error;
    }
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

const api = new ApiClient();
export default api;

// Specific API calls
export const dashboardApi = {
  getStats: () => api.get('/api/dashboard/stats'),
  getTSEStatus: () => api.get('/api/tse/status'),
};

export const transactionsApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/api/transactions${query ? '?' + query : ''}`);
  },
  getById: (id) => api.get(`/api/transactions/${id}`),
  create: (data) => api.post('/api/transactions', data),
  storno: (id, data) => api.post(`/api/transactions/${id}/storno`, data),
};

export const receiptsApi = {
  getHTML: (id) => api.get(`/api/receipts/${id}/html`, { headers: { 'Accept': 'text/html' } }),
  getPDF: (id) => api.get(`/api/receipts/${id}/pdf`, { responseType: 'blob' }),
};

export const portalApi = {
  getOrders: (status) => api.get(`/api/portal/orders${status ? '?status_filter=' + status : ''}`),
  acceptOrder: (id) => api.post(`/api/portal/orders/${id}/accept`),
  webhook: (data) => api.post('/api/portal/webhook', data),
};

export const itemsApi = {
  getAll: (activeOnly = true) => api.get(`/api/items?active_only=${activeOnly}`),
  create: (data) => api.post('/api/items', data),
};

export const exportsApi = {
  createDSFinVK: (data) => api.post('/api/exports/dsfinvk', data, { responseType: 'blob' }),
  createZReport: (data) => api.post('/api/z-report', data),
  getZReports: () => api.get('/api/z-reports'),
};

export const seedApi = {
  seedData: () => api.post('/api/seed-data'),
};

export const settingsApi = {
  get: () => api.get('/api/settings'),
  update: (data) => api.put('/api/settings', data),
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const API_BASE_URL = getBaseURL();
    const response = await fetch(`${API_BASE_URL}/api/settings/logo`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Upload fehlgeschlagen');
    }
    
    return await response.json();
  },
  testPrinter: (printerId) => api.post('/api/settings/printers/test', { printer_id: printerId }),
};
